/**
 * Reine Helfer für den Talent-Status. Kein React, keine Netzwerkzugriffe,
 * damit die Logik ohne Rendering testbar bleibt.
 *
 * Zwei Ebenen:
 *
 * 1. Partner-Ebene, Katalog eigener Status in
 *    Partner.app_settings.dashboard_settings.talent_statuses:
 *    { version: 1, items: [{ id, label: { de, en }, description: { de, en },
 *      color, archived, created_at, created_by }] }
 *    Die 5 Aivy-Status (src/constants/smart-predict.js) bleiben im Code, sie
 *    sind für Auswertung und Vorhersage nötig und lassen sich nicht ändern.
 *
 * 2. Talent-Ebene, ein Eintrag im Array Space.customer_dashboard_meta:
 *    { id: 'TALENT_STATUS', version: 1, status, label, application_status_before,
 *      updated_at, fired_by_id }
 *    Er wird bei jedem Setzen geschrieben, auch für Aivy-Status. So ist der
 *    zuletzt gesetzte Status immer bekannt, obwohl Space.application_status
 *    ein geschlossenes Enum ist und vom Backend überschrieben wird
 *    (ASSESSMENT_COMPLETED, INTEGRATION_*). Der Eintrag gewinnt gegenüber
 *    application_status. Das ist sicher, weil kein Backend-Prozess HIRED,
 *    REJECTED oder einen anderen Nutzer-Status schreibt (geprüft in
 *    aivy-backend-app, Stand September 2026).
 */

import { normalizeMeta } from './employee'
import { upsertMetaEntry } from './table-column-settings'

export const PARTNER_TALENT_STATUSES_KEY = 'talent_statuses'
export const TALENT_STATUSES_VERSION = 1

export const TALENT_STATUS_META_ID = 'TALENT_STATUS'
export const TALENT_STATUS_META_VERSION = 1

export const CUSTOM_STATUS_PREFIX = 'custom_'
// Obergrenze für aktive eigene Status (UI). Archivierte zählen nicht mit,
// sonst würde das Archiv irgendwann jedes Anlegen blockieren.
export const MAX_CUSTOM_STATUSES = 20
// Sicherheitskappe beim Lesen, inkl. archivierter Einträge.
const MAX_STORED_STATUSES = 100
export const MAX_LABEL_LENGTH = 40
export const MAX_DESCRIPTION_LENGTH = 80

export const SUPPORTED_LANGUAGES = ['de', 'en']

// Tailwind JIT braucht literale Klassen, deshalb hier ausgeschrieben statt
// zusammengesetzt. `badge` entspricht den Fällen in src/components/state.js.
export const STATUS_COLORS = {
  blue: { badge: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  green: { badge: 'bg-green-100 text-green-800', dot: 'bg-green-500' },
  yellow: { badge: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-400' },
  red: { badge: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  purple: { badge: 'bg-purple-100 text-purple-800', dot: 'bg-purple-500' },
  pink: { badge: 'bg-pink-100 text-pink-800', dot: 'bg-pink-500' },
  indigo: { badge: 'bg-indigo-100 text-indigo-800', dot: 'bg-indigo-500' },
  grey: { badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-400' }
}

export const DEFAULT_STATUS_COLOR = 'blue'

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const isNonEmptyString = (value) =>
  typeof value === 'string' && value.trim().length > 0

export const isCustomStatusId = (id) =>
  typeof id === 'string' && id.startsWith(CUSTOM_STATUS_PREFIX)

// Übersetzungs-Map { de, en } auf eine Sprache auflösen. Fällt auf Deutsch,
// dann Englisch, dann den ersten vorhandenen Wert zurück.
export const resolveLocalized = (map, language) => {
  if (typeof map === 'string') return map
  if (!isPlainObject(map)) return ''

  const lang = String(language || '').toLowerCase()

  for (const key of [lang, 'de', 'en']) {
    if (isNonEmptyString(map[key])) return map[key].trim()
  }

  const first = Object.values(map).find(isNonEmptyString)

  return first ? first.trim() : ''
}

// Nur nicht-leere Strings der unterstützten Sprachen übernehmen, gekürzt.
const toLocalizedMap = (value, maxLength) => {
  if (isNonEmptyString(value)) {
    return { de: value.trim().slice(0, maxLength) }
  }
  if (!isPlainObject(value)) return {}

  return Object.fromEntries(
    SUPPORTED_LANGUAGES.filter((lang) => isNonEmptyString(value[lang])).map(
      (lang) => [lang, value[lang].trim().slice(0, maxLength)]
    )
  )
}

const normalizeDefinition = (raw) => {
  if (!isPlainObject(raw)) return null
  if (!isCustomStatusId(raw.id)) return null

  const label = toLocalizedMap(raw.label, MAX_LABEL_LENGTH)
  if (!Object.keys(label).length) return null

  return {
    id: raw.id,
    label,
    description: toLocalizedMap(raw.description, MAX_DESCRIPTION_LENGTH),
    color: STATUS_COLORS[raw.color] ? raw.color : DEFAULT_STATUS_COLOR,
    archived: raw.archived === true,
    created_at: isNonEmptyString(raw.created_at) ? raw.created_at : undefined,
    created_by: isNonEmptyString(raw.created_by) ? raw.created_by : undefined
  }
}

// Liest den Katalog aus den geparsten dashboard_settings. Liefert immer ein
// Array (auch archivierte Einträge), dedupliziert nach id, wirft nie.
export const readPartnerTalentStatuses = (dashboardSettings) => {
  const raw = isPlainObject(dashboardSettings)
    ? dashboardSettings[PARTNER_TALENT_STATUSES_KEY]
    : null

  if (!isPlainObject(raw)) return []
  if ((raw.version ?? TALENT_STATUSES_VERSION) !== TALENT_STATUSES_VERSION) {
    return []
  }
  if (!Array.isArray(raw.items)) return []

  const seen = new Set()
  const result = []

  for (const item of raw.items) {
    const definition = normalizeDefinition(item)
    if (!definition || seen.has(definition.id)) continue

    seen.add(definition.id)
    result.push(definition)
    if (result.length >= MAX_STORED_STATUSES) break
  }

  return result
}

export const buildPartnerTalentStatuses = (items) => ({
  version: TALENT_STATUSES_VERSION,
  items
})

// Aus einer Definition das Status-Objekt bauen, das die Tabelle, der Filter
// und die Detailseite erwarten (gleiche Felder wie SMART_PREDICT_SPACE_STATES).
// key bleibt 'SMART_PREDICT', damit bestehende Konsumenten einen gesetzten
// Status erkennen; isCustom markiert die Herkunft.
export const toStatusOption = (definition, index = 0) => ({
  id: definition.id,
  isCustom: true,
  label: definition.label,
  description: definition.description,
  color: definition.color,
  archived: definition.archived,
  // Sortiert hinter den Aivy-Status (11 bis 16), Vergleich ist lexikalisch.
  sortWeight: `16${String(index + 1).padStart(2, '0')}`,
  filterValue: `custom:${definition.id}`
})

// Für eine id, die nicht mehr im Katalog steht, aber noch auf einem Talent
// liegt (Label-Snapshot aus dem Meta-Eintrag).
export const toSnapshotStatusOption = (id, label) =>
  toStatusOption(
    {
      id,
      label: toLocalizedMap(label, MAX_LABEL_LENGTH),
      description: {},
      color: 'grey',
      archived: true
    },
    MAX_STORED_STATUSES
  )

export const findCustomStatus = (customStatuses, id) =>
  (Array.isArray(customStatuses) ? customStatuses : []).find(
    (status) => status.id === id
  )

// Anzeigetext eines Status-Objekts. Aivy-Status tragen i18n-Keys, eigene
// Status eine Übersetzungs-Map. Ein t() auf die Map würde „[object Object]"
// liefern und bei fehlendem Key Bugsnag füllen, deshalb zentral hier.
export const getTalentStatusText = ({ status, t, language, progress }) => {
  if (!status) return ''
  if (status.isCustom) return resolveLocalized(status.label, language)
  if (!status.label) return ''

  return t(status.label, { progress })
}

export const getTalentStatusDescription = ({ status, t, language }) => {
  if (!status || !status.description) return ''
  if (status.isCustom) return resolveLocalized(status.description, language)

  return t(status.description)
}

// Liest den TALENT_STATUS-Eintrag aus dem Meta-Array (oder rohem String).
// Liefert null, wenn nichts gespeichert ist, die Form nicht passt oder die
// Version neuer ist als diese Implementierung.
export const readTalentStatusMeta = (rawMeta) => {
  const entry = normalizeMeta(rawMeta).find(
    (item) => isPlainObject(item) && item.id === TALENT_STATUS_META_ID
  )

  if (!entry) return null
  if (
    (entry.version ?? TALENT_STATUS_META_VERSION) !== TALENT_STATUS_META_VERSION
  ) {
    return null
  }
  if (!isNonEmptyString(entry.status)) return null

  return {
    status: entry.status,
    label: isPlainObject(entry.label) ? entry.label : undefined,
    application_status_before: isNonEmptyString(entry.application_status_before)
      ? entry.application_status_before
      : undefined,
    updated_at: isNonEmptyString(entry.updated_at)
      ? entry.updated_at
      : undefined,
    fired_by_id: isNonEmptyString(entry.fired_by_id)
      ? entry.fired_by_id
      : undefined
  }
}

export const buildTalentStatusMetaEntry = ({
  status,
  label,
  application_status_before,
  fired_by_id,
  now = new Date().toISOString()
}) => {
  const entry = {
    id: TALENT_STATUS_META_ID,
    version: TALENT_STATUS_META_VERSION,
    status,
    updated_at: now
  }

  if (isPlainObject(label) && Object.keys(label).length) entry.label = label
  if (isNonEmptyString(application_status_before)) {
    entry.application_status_before = application_status_before
  }
  if (isNonEmptyString(fired_by_id)) entry.fired_by_id = fired_by_id

  return entry
}

// Meta-Array mit gesetztem (entry) oder entferntem (null) TALENT_STATUS-Eintrag.
// Alle anderen Einträge bleiben unverändert. Rückgabe ist der String für
// updateSpace.customer_dashboard_meta.
export const withTalentStatusMeta = (rawMeta, entryOrNull) =>
  JSON.stringify(
    upsertMetaEntry(normalizeMeta(rawMeta), TALENT_STATUS_META_ID, entryOrNull)
  )

// Case-insensitive Vergleich für die Eindeutigkeit von Bezeichnungen.
export const normalizeLabelForCompare = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
